<template>
<div class="fehler-markieren">

    <p class="auftrag">
        {{ fehlerListe.length === 1 ? 'In diesem Funkverkehr steckt ein Fehler.' : `In diesem Funkverkehr stecken ${fehlerListe.length} Fehler.` }}
        Tippe die falschen Bausteine an.
    </p>

    <AblaufKopf :uebung="uebung" />

    <div v-for="z in uebung.zuege" :key="z.nr" class="zug" :style="einzug(z)">
        <p v-if="uebung.zuege.length > 1" class="station nur-vorlesen">{{ z.station.rufname }}</p>
        <div class="bausteine">
            <button v-for="t in teileVon(z)" :key="t.id"
                :class="{ markiert: markiert.has(t.id), gefunden: geloest && falsch.has(t.id) }"
                :aria-pressed="markiert.has(t.id)"
                :disabled="geloest"
                @click="umschalten(t.id)">
                {{ t.text }}
            </button>
        </div>
    </div>

    <!-- Rueckmeldung -->
    <div v-if="rueckmeldung" class="rueckmeldung" :class="rueckmeldung.sitzt ? 'stimmt' : 'daneben'">
        <template v-if="rueckmeldung.sitzt">
            <p class="satz">Alle Fehler gefunden.</p>
            <ul>
                <li v-for="f in fehlerListe" :key="f.falsch[0]">
                    «{{ textVon(f) }}» statt «{{ f.richtig[0] }}»: {{ f.regeln[0] }}
                </li>
            </ul>
        </template>
        <template v-else>
            <p class="satz">Das stimmt noch nicht.</p>
            <ul>
                <li v-if="rueckmeldung.zuviel">
                    {{ rueckmeldung.zuviel === 1 ? 'Ein markierter Baustein ist richtig.' : `${rueckmeldung.zuviel} markierte Bausteine sind richtig.` }}
                </li>
                <li v-if="rueckmeldung.uebersehen">
                    {{ rueckmeldung.uebersehen === 1 ? 'Ein Fehler ist noch nicht markiert.' : `${rueckmeldung.uebersehen} Fehler sind noch nicht markiert.` }}
                </li>
            </ul>
        </template>
    </div>

    <div class="knopfleiste">
        <button class="haupt" @click="pruefen()" :disabled="geloest || (leerSperren && markiert.size === 0)">Prüfen</button>
        <button @click="zuruecksetzen()" :disabled="geloest">Zurücksetzen</button>
        <slot name="rechts" />
    </div>

</div>
</template>

<script>
import { ref, computed } from 'vue';
import AblaufKopf from '@/components/AblaufKopf.vue';
import { einzugFuer } from '@/logic/Einzug';

// Uebungsart "Fehler markieren": der ganze Funkverkehr steht fertig da, in
// einem oder zwei Zuegen steckt je ein Fehler. Gesucht sind die Bausteine,
// die nicht hineingehoeren. Darum nur Fehler der Art "ersatz" — ein Tausch
// hat keinen einzelnen falschen Baustein, den man antippen koennte.
//
// Vertrag mit dem Container wie bei jeder Uebungsart (siehe AllesSortieren).
// Pruefen ist hier einmalig: sitzt es, ist die Uebung geloest.
// Die Fehler sind schon gewuerfelt: uebung.fehlersuche, siehe bereiteVor().
export default {
    components: { AblaufKopf },
    // Der Container reicht allen Ansichten dieselben Props; was diese nicht
    // braucht, soll nicht als HTML-Attribut am Wurzelelement landen.
    inheritAttrs: false,
    props: {
        uebung: {type: Object, required: true},
        leerSperren: {type: Boolean, default: false}
    },
    emits: ['geprueft'],
    setup(props, { emit }) {
        const { fehler } = props.uebung.fehlersuche
        const fehlerListe = Object.values(fehler)
        const falsch = new Set(fehlerListe.flatMap(f => f.falsch))

        const teileVon = (z) => fehler[z.nr] ? fehler[z.nr].teile : z.teile
        const textVon = (f) => f.teile.find(t => t.id === f.falsch[0]).text
        const einzug = einzugFuer(props.uebung)

        // Ein Set in einem ref: Vue beobachtet add/delete, darum reicht das.
        const markiert = ref(new Set())
        const rueckmeldung = ref(null)
        const geloest = computed(() => rueckmeldung.value?.sitzt === true)

        const umschalten = (id) => {
            if (markiert.value.has(id)) markiert.value.delete(id)
            else markiert.value.add(id)
            rueckmeldung.value = null
        }

        const zuruecksetzen = () => {
            markiert.value = new Set()
            rueckmeldung.value = null
        }

        const pruefen = () => {
            const zuviel = [...markiert.value].filter(id => !falsch.has(id)).length
            const uebersehen = [...falsch].filter(id => !markiert.value.has(id)).length
            const sitzt = zuviel === 0 && uebersehen === 0
            emit('geprueft', { sitzt, geloest: sitzt })
            rueckmeldung.value = { sitzt, zuviel, uebersehen }
        }

        return { fehlerListe, falsch, teileVon, textVon, einzug, markiert, rueckmeldung, geloest,
                 umschalten, zuruecksetzen, pruefen }
    }
}
</script>

<style scoped>
.auftrag {
    margin: 0 0 1.25rem;
    font-weight: 600;
    color: var(--zs-tinte);
}

/* Station = Farbe Blau, wie ueberall: Farbe steht fuer Station oder fuer
   richtig/falsch, fuer nichts sonst. */
.station {
    margin: 0 0 0.35rem;
    font-size: 0.8rem;
    font-weight: 650;
    color: var(--zs-blau);
}

.zug {
    margin: 0 0 1rem;
}

.bausteine {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
}

.bausteine button {
    min-height: 2.5rem;
    padding: 0.5rem 0.85rem;
    font: inherit;
    line-height: 1.4;
    color: var(--zs-tinte);
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
    cursor: pointer;
    transition: border-color 0.15s, background-color 0.15s;
}

.bausteine button:hover:not(:disabled) {
    border-color: var(--zs-tinte);
}

/* Markiert = ausgewaehlt: Orange, durchgestrichen. */
.bausteine button.markiert {
    background: var(--zs-orange-hell);
    border-color: var(--zs-orange);
    box-shadow: inset 0 0 0 1px var(--zs-orange);
    text-decoration: line-through;
}

.bausteine button:disabled {
    cursor: default;
}

.bausteine button.gefunden {
    background: var(--zs-oliv-hell);
    border-color: var(--zs-oliv);
    box-shadow: inset 0 0 0 1px var(--zs-oliv);
}

/* ─── Rueckmeldung ───────────────────────────────────────────
   Nie nur Farbe: vor dem Satz steht ein Haken oder ein Kreuz. */

.rueckmeldung {
    margin: 1.5rem 0 0;
    padding: 0.85rem 1rem;
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
}

.rueckmeldung.stimmt {
    background: var(--zs-oliv-hell);
    border-color: rgba(85, 107, 47, 0.45);
}

.rueckmeldung.daneben {
    background: var(--zs-warnrot-hell);
    border-color: rgba(179, 38, 30, 0.4);
}

.satz {
    margin: 0;
    font-weight: 650;
}

.stimmt .satz {
    color: var(--zs-oliv);
}

.daneben .satz {
    color: var(--zs-warnrot);
}

.stimmt .satz::before {
    content: '\2713\00a0\00a0';
}

.daneben .satz::before {
    content: '\2715\00a0\00a0';
}

.rueckmeldung ul {
    margin: 0.5rem 0 0;
    padding-left: 1.6rem;
}

.rueckmeldung li {
    margin-bottom: 0.2rem;
    line-height: 1.45;
    color: var(--zs-tinte);
}
/* ─── Knopfleiste ────────────────────────────────────────────
   Rechts kommt, was dem Container gehoert (Neue Uebung / Weiter). */

.knopfleiste {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid var(--zs-rahmen);
}

.knopfleiste button {
    min-height: 2.5rem;
    padding: 0.45rem 1rem;
    font: inherit;
    font-weight: 600;
    color: var(--zs-tinte);
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
    cursor: pointer;
}

.knopfleiste button:hover:not(:disabled) {
    border-color: var(--zs-tinte);
}

/* Die eigentliche Handlung der Uebung (Pruefen / Senden). */
.knopfleiste .haupt {
    color: #ffffff;
    background: var(--zs-blau);
    border-color: var(--zs-blau);
}

.knopfleiste .haupt:hover:not(:disabled) {
    background: var(--zs-blau-dunkel);
    border-color: var(--zs-blau-dunkel);
}

.knopfleiste button:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

button:focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: 2px;
}

/* Funktext in fester Laufweite: Protokolltext, nicht Bedientext. */
.bausteine button {
    font-family: var(--zs-funkschrift);
}

@media (prefers-reduced-motion: reduce) {
    .bausteine button {
        transition: none;
    }
}
</style>
